import type * as React from 'react'

import _ from 'lodash'

import {
  RegexFor12H,
  RegexFor24H,
  getStringValueWithMeridiem,
  getStringValueWithoutMeridiem,
} from 'modules/core/lib/formatTime'

import setNativeValue from './setNativeValue'
import sortedNumberKeys from './sortedNumberKeys'

// TODO: fix these types after TS migration
// type Segment = 'hrs' | 'min' | 'meridiem'
// type Range = {
//   [key: Segment]: { start: number, end: number }
// }
// type UpperLimit = {
//   [key: Segment]: Array<number>
// }
const RANGES: any = {
  hrs: {
    start: 0,
    end: 2,
  },
  min: {
    start: 3,
    end: 5,
  },
  meridiem: {
    start: 7,
    end: 9,
  },
}

const UPPER_LIMITS_FOR_12H: any = {
  hrs: [1, 2],
  min: [5, 9],
}

const UPPER_LIMITS_FOR_24H: any = {
  hrs: [2, 3],
  min: [5, 9],
}

const EMPTY_STATE = '--'
const AM = 'AM'
const PM = 'PM'
const MIN = 'min'
const HRS = 'hrs'
const MERIDIEM = 'meridiem'
const DEFAULT_MERIDIEM = AM
const MANUAL_ENTRY_LOG: string[] = []
const leadingZero = (num: string): string => _.padStart(num, 2, '0')

const convertNumber = (num: any): string => (isNaN(num) ? num : String(parseInt(num)))

export const getDefaultString = (isTimeFormat24H: boolean) =>
  getStringValue(
    {
      hrs: EMPTY_STATE,
      min: EMPTY_STATE,
      meridiem: DEFAULT_MERIDIEM,
    },
    isTimeFormat24H
  )

const getStringValue = (
  inputValues: { hrs: string; min: string; meridiem: string },
  isTimeFormat24H: boolean
) => {
  if (isTimeFormat24H) {
    return getStringValueWithoutMeridiem({
      hrs: inputValues.hrs,
      min: inputValues.min,
    })
  }

  return getStringValueWithMeridiem({
    hrs: inputValues.hrs,
    min: inputValues.min,
    meridiem: inputValues.meridiem,
  })
}

class State {
  input: React.ElementRef<'input'>
  segment: any
  isTimeFormat24H: boolean

  constructor(input: React.ElementRef<'input'>, isTimeFormat24H: boolean) {
    this.input = input
    this.setSegmentByCursor()
    this.isTimeFormat24H = isTimeFormat24H
  }

  get inputValues(): any {
    const value = this.input.value

    const result = !this.isTimeFormat24H ? RegexFor12H.exec(value) : RegexFor24H.exec(value)

    // This should never happen, but flow rightfully complains.
    if (!result) {
      return {
        hrs: EMPTY_STATE,
        min: EMPTY_STATE,
        meridiem: DEFAULT_MERIDIEM,
      }
    }

    return {
      hrs: convertNumber(result[1]),
      min: convertNumber(result[2]),
      meridiem: result[3] || DEFAULT_MERIDIEM,
    }
  }

  get currentSegmentUpperLimits() {
    if (this.isTimeFormat24H) return UPPER_LIMITS_FOR_24H[this.segment]

    return UPPER_LIMITS_FOR_12H[this.segment]
  }

  get currentSegmentRanges() {
    return RANGES[this.segment]
  }

  get isHrs(): boolean {
    return this.segment === HRS
  }

  get isMeridiem(): boolean {
    return this.segment === MERIDIEM
  }

  get isMinute(): boolean {
    return this.segment === MIN
  }

  clear() {
    this._set(EMPTY_STATE)
  }

  next() {
    MANUAL_ENTRY_LOG.length = 0

    switch (this.segment) {
      case MERIDIEM:
        return

      case MIN:
        if (this.isTimeFormat24H) return

        this.segment = MERIDIEM
        break

      case HRS:
        this.segment = MIN
    }

    this.setSelectionRangeFromSegment()
  }

  prev() {
    MANUAL_ENTRY_LOG.length = 0

    switch (this.segment) {
      case HRS:
        return

      case MIN:
        this.segment = HRS
        break

      case MERIDIEM:
        this.segment = MIN
    }

    this.setSelectionRangeFromSegment()
  }

  setSelectionRangeFromSegment() {
    const { start, end } = this.currentSegmentRanges
    this.input.setSelectionRange(start, end)
  }

  reset() {
    setNativeValue(this.input, getDefaultString(this.isTimeFormat24H))

    this._triggerEvents()

    this.segment = HRS
    this.setSelectionRangeFromSegment()
    MANUAL_ENTRY_LOG.length = 0
  }

  changeToPM() {
    if (this.isMeridiem) this._set(PM)
  }

  changeToAM() {
    if (this.isMeridiem) this._set(AM)
  }

  nudge(modifier: number) {
    let value: any = this.inputValues[this.segment]
    let newValue

    if (this.isMeridiem) {
      newValue = this.inputValues[this.segment] === AM ? PM : AM

      this._set(newValue)

      return
    }

    const limit = parseInt(this.currentSegmentUpperLimits.join(''))

    if (value === EMPTY_STATE) value = 0

    newValue = (parseInt(value) + modifier) % (limit + 1)
    newValue = newValue < 0 ? limit : newValue

    // There isn't any 00:30 AM in 12H system, instead it should be 12:30 AM
    if (newValue === 0 && !this.isTimeFormat24H && this.isHrs) {
      newValue = +value === 12 ? 1 : 12
    }

    this._set(leadingZero(String(newValue)))
  }

  write(key: number) {
    if (this.isMeridiem) return

    const keyValue = sortedNumberKeys[key]
    const entryCount = MANUAL_ENTRY_LOG.length
    const limit = this.currentSegmentUpperLimits[entryCount]

    if (entryCount < 2) {
      MANUAL_ENTRY_LOG.push(keyValue)
    }

    const fullLimit = parseInt(this.currentSegmentUpperLimits.join(''))
    const fullEntry = parseInt(MANUAL_ENTRY_LOG.join(''))

    if (fullLimit >= fullEntry) {
      this._set(String(fullEntry))
    }

    const atLimit = keyValue > limit || MANUAL_ENTRY_LOG.length === 2

    if (atLimit) {
      // users should be able to type 00, but after changing the focus to minutes they
      // should see 01. This is the only case that we allow users to enter 00 in the UI.
      // based on https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/time
      if (fullEntry === 0 && !this.isTimeFormat24H && this.isHrs) {
        this._set(String(1))
      }

      this.next()
    }
  }

  setSegmentByCursor(): State {
    MANUAL_ENTRY_LOG.length = 0

    const selection: any = {
      start: this.input.selectionStart,
      end: this.input.selectionEnd,
    }

    // eslint-disable-next-line
    for (const segment in RANGES) {
      const range = RANGES[segment]
      const aboveMin = range.start <= selection.start
      const belowMax = range.end >= selection.end

      if (aboveMin && belowMax) {
        this.segment = segment
        MANUAL_ENTRY_LOG.length = 0

        return this
      }
    }

    this.segment = HRS

    return this
  }

  getCurrentString() {
    return getStringValue(this.inputValues, this.isTimeFormat24H)
  }

  // private
  _set(value: string) {
    const inputValues = this.inputValues
    inputValues[this.segment] = value

    const newInputVal = getStringValue(inputValues, this.isTimeFormat24H)
    setNativeValue(this.input, newInputVal)
    this.setSelectionRangeFromSegment()

    this._triggerEvents()
  }

  _triggerEvents() {
    ;['input', 'change'].forEach((eventName: string) => {
      let event

      if (typeof Event === 'function') {
        event = new Event(eventName, {
          bubbles: true,
        })
      } else {
        event = document.createEvent('Event')
        event.initEvent(eventName, true, true)
      }

      this.input.dispatchEvent(event)
    })
  }
}

export default State
